/**
 * API client for 加療期間切れリスト (treatment_period).
 * A2/R2 dispatched via `X-Service-Type` header — service functions are identical for both.
 */

import { apiClient } from '@shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

import type { ApiResponse } from './index';
import type {
  ConsentTreatmentPeriodListParams,
  ConsentTreatmentPeriodListResponse,
  ConsentTreatmentPeriodMassagersResponse,
  ConsentTreatmentPeriodPrintPayload,
} from '@/types/consentTreatmentPeriod.types';

const BASE = '/consents/treatment-period';

function toQuery(params?: Record<string, unknown> | object): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (!params) return out;
  Object.entries(params as Record<string, unknown>).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      out[k] = v as string | number;
    }
  });
  return out;
}

function authHeaders(serviceType: string) {
  return { 'X-Service-Type': serviceType };
}

export async function getTreatmentPeriodList(
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  params?: ConsentTreatmentPeriodListParams,
): Promise<ApiResponse<ConsentTreatmentPeriodListResponse>> {
  const res = await apiClient.get<ApiResponse<ConsentTreatmentPeriodListResponse>>(
    clinicApiPath(siteType, clinicNumber, BASE),
    { params: toQuery(params), headers: authHeaders(serviceType) },
  );
  return res.data;
}

export async function getTreatmentPeriodMassagers(
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  params: { search_treatement_from?: string; search_treatement_to?: string },
): Promise<ApiResponse<ConsentTreatmentPeriodMassagersResponse>> {
  const res = await apiClient.get<ApiResponse<ConsentTreatmentPeriodMassagersResponse>>(
    clinicApiPath(siteType, clinicNumber, `${BASE}/massagers`),
    { params: toQuery(params), headers: authHeaders(serviceType) },
  );
  return res.data;
}

export interface BinaryDownload {
  blob: Blob;
  filename: string;
  contentType: string;
}

interface PreviewTicketResponse {
  success: boolean;
  message: string;
  data: { url: string };
}

function parseFilename(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  // Support both "filename=..." and RFC 5987 "filename*=UTF-8''..." forms.
  const star = /filename\*=(?:UTF-8''|)([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1].replace(/"/g, ''));
  const simple = /filename="?([^";]+)"?/i.exec(disposition);
  return simple?.[1] ?? fallback;
}

async function postBinary(
  url: string,
  payload: unknown,
  serviceType: string,
  fallbackName: string,
): Promise<BinaryDownload> {
  const res = await apiClient.post<Blob>(url, payload, {
    headers: authHeaders(serviceType),
    responseType: 'blob',
  });
  return {
    blob: res.data,
    filename: parseFilename(res.headers['content-disposition'] as string | undefined, fallbackName),
    contentType: (res.headers['content-type'] as string | undefined) ?? 'application/octet-stream',
  };
}

export function postTreatmentPeriodListPdf(
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  payload: ConsentTreatmentPeriodPrintPayload,
): Promise<BinaryDownload> {
  return postBinary(
    clinicApiPath(siteType, clinicNumber, `${BASE}/list-pdf`),
    payload,
    serviceType,
    'expired_list.pdf',
  );
}

export async function createTreatmentPeriodListPdfPreviewTicket(
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  payload: ConsentTreatmentPeriodPrintPayload,
): Promise<string> {
  const res = await apiClient.post<PreviewTicketResponse>(
    clinicApiPath(siteType, clinicNumber, `${BASE}/list-pdf/preview-ticket`),
    payload,
    { headers: authHeaders(serviceType) },
  );

  return res.data.data.url;
}

export function postTreatmentPeriodLabelPdf(
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  payload: ConsentTreatmentPeriodPrintPayload,
): Promise<BinaryDownload> {
  return postBinary(
    clinicApiPath(siteType, clinicNumber, `${BASE}/label-pdf`),
    payload,
    serviceType,
    'expired_label.pdf',
  );
}

export function postTreatmentPeriodCsv(
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  payload: ConsentTreatmentPeriodPrintPayload,
): Promise<BinaryDownload> {
  return postBinary(
    clinicApiPath(siteType, clinicNumber, `${BASE}/csv`),
    payload,
    serviceType,
    'consent_term_list.csv',
  );
}
